import { useTranslation } from 'react-i18next'
import { sanitizeCoachText } from '../../lib/aiResponseValidator'
import { weekdayFromLocalIsoDate } from '../../lib/resolveMorningBrief'
import { KILO } from '../../constants/KILO_THEME'

type BodyStateBriefingProps = {
  briefingText: string | null
  adjustments: Record<string, unknown>
  date: string
  isScheduledRest?: boolean
  embedded?: boolean
  awaitingAck?: boolean
  onAcknowledge?: () => void
}

const BodyStateBriefing = ({
  briefingText,
  adjustments,
  date,
  isScheduledRest = false,
  embedded = false,
  awaitingAck = false,
  onAcknowledge,
}: BodyStateBriefingProps) => {
  const { t, i18n } = useTranslation()
  const safeBriefing = sanitizeCoachText(briefingText, 'morning_briefing')

  const isoDate = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : date.slice(0, 10)
  const weekday = weekdayFromLocalIsoDate(isoDate, i18n.language)
  const volumeAdj = adjustments?.workout_volume_adjustment_pct as number | undefined
  const isBiometricRest = Boolean(adjustments?.rest_day)
  const isRestDay = isScheduledRest || isBiometricRest
  const volumeSign = volumeAdj != null && volumeAdj > 0 ? '+' : ''

  const emptyShell = embedded
    ? KILO.embeddedSection
    : `col-span-2 rounded-2xl border border-dashed border-kilo-border bg-kilo-card p-5`
  const filledShell = embedded
    ? KILO.embeddedSection
    : 'col-span-2 rounded-2xl border border-kilo-accent/20 bg-kilo-card p-5'

  if (!safeBriefing) {
    return (
      <div className={emptyShell}>
        <p className={`mb-3 ${KILO.sectionLabel}`}>{t('coach.briefing.title')}</p>
        <p className={`text-sm leading-relaxed text-neutral-500 dark:text-neutral-400`}>
          {t('coach.briefing.emptyState')}
        </p>
        {awaitingAck && onAcknowledge ? (
          <button
            type="button"
            onClick={onAcknowledge}
            className="mt-4 flex h-11 w-full items-center justify-center rounded-xl bg-kilo-accent text-sm font-bold text-black"
          >
            {t('coach.briefing.continue')}
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div className={filledShell}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className={KILO.sectionLabel}>{t('coach.briefing.header', { weekday })}</p>
        <div className="flex gap-2">
          {isRestDay ? (
            <span className="rounded-full border border-kilo-line bg-kilo-card px-2 py-0.5 text-xs text-kilo-text">
              {t('coach.briefing.restDay')}
            </span>
          ) : null}
          {volumeAdj != null && !isRestDay ? (
            <span className={KILO.volumeBadge}>
              {t('coach.briefing.volume', { sign: volumeSign, pct: volumeAdj })}
            </span>
          ) : null}
        </div>
      </div>
      <p className={`text-sm leading-relaxed ${KILO.text.primary}`}>{safeBriefing}</p>
      {awaitingAck && onAcknowledge ? (
        <button
          type="button"
          onClick={onAcknowledge}
          className="mt-4 flex h-11 w-full items-center justify-center rounded-xl bg-kilo-accent text-sm font-bold text-black"
        >
          {t('coach.briefing.continue')}
        </button>
      ) : null}
    </div>
  )
}

export default BodyStateBriefing
